import { Stepper } from "../components/Stepper";
import { useForm, FormProvider } from "react-hook-form";
import { useContext, useEffect, useRef, useCallback, useState } from "react";
import { themeCtx } from "../../utils/hooks/themeContext";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import TypeOfUnit from "./subforms/TypeOfUnit";
import OperatingPoint from "./subforms/OperatingPoint1";
import Coils from "./subforms/Coils";
import Filtering from "./subforms/Filtering";
import Fan from "./subforms/Fan";
import Calculation from "./subforms/Calculation";
import UnitInformation from "./subforms/UnitInformation";
import Loader from "../../components/Loader";
import {
  getAirFlowCalculation,
  getProject,
  getProjectLocationConditions,
  createProjectItemBlob,
  editProjectItem,
  getProjectItem,
} from "../../utils/services/projectService";
import {
  fanValApi,
  heatValApi,
  recValApi,
  coilsValApi,
  damperValApi,
  modularFilterApi,
  ziehlAbeggFanApi,
  ziehlAbeggFanCalculateApi,
  ziehlAbeggFanSelectApi,
} from "../../utils/services/dllService";
import { setSnackbar } from "../../actions/snackbarActions";
import { useDispatch } from "react-redux";
import { Steps } from "../../utils/hooks/useSteps";
import config from "../../utils/config";
import { useFormFieldCalculations } from "../../utils/hooks/useFormFieldCalculations";
import HeatRecovery from "./subforms/HeatRecovery";
import HeatPump from "./subforms/HeatPump";
import Connection from "./subforms/Connection";
import LinePrimeFiltering from "./subforms/LinePrimeFiltering";
import LinePrimeFan from "./subforms/LinePrimeFan";
import { AhuType } from "../../models/unitType";
import { setFormValues } from "../../utils/services/setFormValues";
import { unitTypeReverseMapping } from "../../utils/unitConstants";

export const Configurator = () => {
  const { currentTheme } = useContext(themeCtx);
  const { t } = useTranslation();
  const snackbarDispatch = useDispatch();
  const isFirstRender = useRef(true);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;
  const projectId = params?.projectId;
  const isEditing = searchParams.get("isEditing") === "true"; // For editing existing items
  const projectItemId = searchParams.get("projectItemId");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  const dispatchError = useCallback(
    (message) => {
      snackbarDispatch(setSnackbar(true, "error", message, "", {}));
    },
    [snackbarDispatch],
  );

  // Helper function to get coilVal for request payload
  const getCoilVal = useCallback((coils, baseUnitType, unitType) => {
    if (baseUnitType === 2 && unitType === "Energy:Prime Standard") {
      return "both";
    }
    return coils;
  }, []);

  // Helper function to get recVal for request payload
  const getRecVal = useCallback((heatRecovery, baseUnitType) => {
    if (baseUnitType === 2 || baseUnitType === 4) {
      return "PPL_4";
    }
    if (baseUnitType === 6) {
      return "without";
    }
    return heatRecovery;
  }, []);

  const toNumber = useCallback((value) => {
    if (value === "" || value === null || value === undefined) {
      return 0;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  }, []);

  const buildConnectionAccessory = useCallback(
    (width, height, isEnabled, airVelocity) => ({
      width: toNumber(width),
      height: toNumber(height),
      isEnabled: Boolean(isEnabled),
      airVelocity: toNumber(airVelocity),
    }),
    [toNumber],
  );

  const getAccessoryField = useCallback((result, keys) => {
    for (const key of keys) {
      const value = result?.[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }

    return undefined;
  }, []);

  const extractConnectionValuesFromRecCalculation = useCallback(
    (calculation) => {
      if (!calculation) {
        return {};
      }

      const nextValues = {};
      const mapDamperValues = (fieldPrefix, damperKeys) => {
        const damper = getAccessoryField(calculation, damperKeys);

        if (!damper || typeof damper !== "object") {
          return;
        }

        const width = getAccessoryField(damper, ["damperWidth", "DamperWidth"]);
        const height = getAccessoryField(damper, [
          "damperHeight",
          "DamperHeight",
        ]);
        const airVelocity = getAccessoryField(damper, [
          "airVelocity",
          "AirVelocity",
        ]);

        if (width !== undefined) {
          nextValues[`${fieldPrefix}Width`] = width.toString();
          nextValues[`${fieldPrefix}WidthMax`] = width.toString();
        }

        if (height !== undefined) {
          nextValues[`${fieldPrefix}Height`] = height.toString();
        }

        if (airVelocity !== undefined) {
          nextValues[`${fieldPrefix}AirVelocity`] = airVelocity.toString();
        }

        nextValues[`${fieldPrefix}Damper`] = true;
      };

      mapDamperValues("connectionFreshAir", [
        "freshRegulationDamper",
        "FreshRegulationDamper",
      ]);
      mapDamperValues("connectionSupplyAir", [
        "supplyRegulationDamper",
        "SupplyRegulationDamper",
      ]);

      const returnAirDamper = getAccessoryField(calculation, [
        "returnRegulationDamper",
        "ReturnRegulationDamper",
      ]);

      if (returnAirDamper && typeof returnAirDamper === "object") {
        const returnWidth = getAccessoryField(returnAirDamper, [
          "damperWidth",
          "DamperWidth",
        ]);
        const returnHeight = getAccessoryField(returnAirDamper, [
          "damperHeight",
          "DamperHeight",
        ]);
        const returnAirVelocity = getAccessoryField(returnAirDamper, [
          "airVelocity",
          "AirVelocity",
        ]);
        const hasReturnDamperValues =
          returnWidth !== undefined && returnHeight !== undefined;

        nextValues.connectionReturnAirEnabled = false;

        if (hasReturnDamperValues) {
          nextValues.connectionReturnAirWidth = returnWidth.toString();
          nextValues.connectionReturnAirWidthMax = returnWidth.toString();
          nextValues.connectionReturnAirHeight = returnHeight.toString();
          if (returnAirVelocity !== undefined) {
            nextValues.connectionReturnAirAirVelocity =
              returnAirVelocity.toString();
          }
          nextValues.connectionReturnAirDamper = true;
        }
      }

      return nextValues;
    },
    [getAccessoryField],
  );

  const methods = useForm({
    defaultValues: {
      projId: projectId,
      projIdent: 123,
      projName: "",
      projNumber: 1,
      revisionNumber: 0,
      area: 100,
      height: 2.8,
      buildingType: "Residential - Basements (3-4)",
      changeRate: 4,
      dropSupply: 250,
      flowSupply: baseUnitType === AhuType.LINE_PRIME ? 18000 : 1120,
      flowReturn: baseUnitType === AhuType.LINE_PRIME ? 18000 : 1120,
      dropReturn: 250,
      // Unit Information calculator mode
      airFlowCalc: true,
      // Requirements (moved to Type of Unit)
      ecoDesign: "compliant",
      casingQuality: "XPS",
      // Type of unit
      installation: "",
      Type: baseUnitType ? baseUnitType : AhuType.ENERGY_PRIME_STANDARD,
      coils: "",
      heatRecovery: "", // "Rotary heat exchanger" bilo
      sizeOfUnit: "",
      controls: t("unitTypeList.controls"),
      unitDivision: "transport3",
      heatRecoveryPlateType: "Rotary", // "Rotary" bilo

      airflowDirection: 1,
      recCalculationResponse: null,
      // Operating point
      //summer
      summerOutdoorTemperature: 0,
      summerWetBulbTemperature: 0,
      summerDewPointTemperature: 0,
      summerOutdoorHumidity: 0,
      summerIndoorTemperature: 26,
      summerIndoorHumidity: 50,
      summerSupplyTemperature: 18,
      summerRecTemperatureOut: 0,
      summerRecHumidityOut: 0,
      // Heat Recovery recVal response data
      summerRecCalculation: null,
      winterRecCalculation: null,
      coolerType: "water",
      coolerConcentration: 0,
      coolerTemperatureIn: 7,
      //coolerTemperatureOut: 12,
      designDryBulbTemperature: 34.6,
      designDewPointTemperature: 14.3,
      designWetBulbTemperature: 21.1,
      //winter
      winterOutdoorTemperature: 0,
      winterOutdoorHumidityRatio: 1,
      elevation: 1,
      winterOutdoorHumidity: 0,
      winterIndoorTemperature: 20,
      winterIndoorHumidity: 40,
      winterSupplyTemperature: 22,
      winterRecTemperatureOut: 0,

      heaterType: "water",
      heaterConcentration: 0,
      heaterTemperatureIn: 35,
      //heaterTemperatureOut: 28,

      // Filtering
      doubleStageFiltering: "No",
      filteringAdvancedSettings: false,
      fineFilterType: "Soft bag filter",
      freshAirFilterClass: "M6",
      supplyAirFilterClass: "F7",
      returnAirFilterClass: "M6",
      // Fan
      fanAdvancedSettings: "On",
      fanType: "Premium",
      fanMode: "Premium",
      damperHeight: "211",
      damperWidth: "370",
      fanInstallationHeight: "407",
      fanInstallationWidth: "600",
      internalHeight: "853",
      internalWidth: "600",
      externalWidth: "704",
      externalHeight: "957",
      unitFilter: "Opakfil Basic ST;M6/F7;592x287x296",
      fan: "K3G250PR04H3;ECF(K)6E250-PLHDAL4Y-PRF",
      heatRecoverySize: 0,
      guid: crypto.randomUUID(),
      coilsWidth: "600",
      coilsHeight: "407",
      //coils
      airInputParamsHeater: "Supply Temperature",
      winterSupplyTemperatureState: "Supply Temperature",
      airInputParamsCooler: "Supply Temperature",
      summerSupplyTemperatureState: "Supply Temperature",
      SummerAirTemperatureIn: 0,
      WinterAirTemperatureIn: 0,
      coilInputTemperatureCooler: 0,
      coilInputTemperatureHeater: 0,
      coilInputHumidityCooler: 0,
      coilInputHumidityHeater: 0,
      airTempInHeat: false,
      airTempInCool: false,
      heaterAirInputParam: "Supply Temperature",
      heaterAirInputParamValue: 22,
      heaterWaterInputParam: "Temperature out",
      heaterWaterInputParamValue: 28,
      coolerAirInputParam: "Supply Temperature",
      coolerAirInputParamValue: 22,
      coolerWaterInputParam: "Temperature out",
      coolerWaterInputParamValue: 12,
      // Store unitTypes from API response
      sizeOfUnitTypes: [],

      // Additional Equipment (Compact PRIME)
      Automatics: true,
      Energetics: true,
      AirflowLeading: true,
      ServiceOpening: false,
      EnergyVAV: false,
      EnergyVAVCount: 0,
      Lights: false,
      Roof: false,
      SpareFilters: false,
      PressureLeading: false,
      Periphery: 2,
      HeaterMixingValve: false,
      CoolerMixingValve: false,

      // Heat Pump
      heatPumpTemperatureInWinter: -8,
      heatPumpTemperatureInSummer: 10,
      heatPumpHumidityInWinter: 35,
      heatPumpHumidityInSummer: 55,
      heatPumpSetPointWinter: 23,
      heatPumpSetPointSummer: 16,
      heatPumpMediumType: "R454B",
      heatPumpMediumContentration: "0%",
      heatPumpEvaporatingWinter: -8,
      heatPumpCondensingWinter: 40,
      heatPumpEvaporatingSummer: 10,
      heatPumpCondensingSummer: 45,
      // Heat Pump Winter Medium Data
      heatPumpHeaterType: "water",
      heatPumpHeaterConcentration: 0,
      heatPumpHeaterTemperatureIn: 35,
      heatPumpHeaterWaterInputParam: "Temperature out",
      heatPumpHeaterWaterInputParamValue: 28,
      // Heat Pump UI state
      heatPumpLoading: false,
      heatPumpError: null,
      heatPumpHumidityAfterWinter: null,
      heatPumpTemperatureAfterWinter: null,

      // LINE:PRIME Connection
      connectionManualChange: false,
      connectionFreshAirWidth: "",
      connectionFreshAirWidthMax: "",
      connectionFreshAirHeight: "",
      connectionFreshAirAirVelocity: "",
      connectionFreshAirDamper: true,
      connectionFreshAirFlexible: false,
      connectionSupplyAirWidth: "",
      connectionSupplyAirWidthMax: "",
      connectionSupplyAirHeight: "",
      connectionSupplyAirAirVelocity: "",
      connectionSupplyAirDamper: true,
      connectionSupplyAirFlexible: false,
      connectionReturnAirEnabled: false,
      connectionReturnAirWidth: "",
      connectionReturnAirWidthMax: "",
      connectionReturnAirHeight: "",
      connectionReturnAirAirVelocity: "",
      connectionReturnAirDamper: true,
      connectionReturnAirFlexible: false,
      connectionReturnAirMixingRatio: 0,

      // LINE:PRIME Filtering
      linePrimeFilterFirstStageEnabled: true,
      linePrimeFilterFirstStageClass: "M5",
      linePrimeFilterSecondStageEnabled: false,
      linePrimeFilterSecondStageClass: "F7",

      // LINE:PRIME Fan
      linePrimeMotorType: "",
      linePrimeFanTableData: [],
      linePrimeSelectedFan: null,
      linePrimeFanCalculated: false,
      linePrimeFanCalculating: false,
      fanChartImage: null,
      linePrimeFanCalculateRequested: false,

      // LINE:PRIME Type of Unit
      servicingSide: "",  
    },
  });

  const setValues = useCallback(
    (values) => {
      setFormValues(methods.setValue)(values);
    },
    [methods.setValue],
  );

  const formStorageKey = `configurator-form-${projectId}${projectItemId ? `-${projectItemId}` : ""}`;
  const isRestoredRef = useRef(false);

  // Save current form state to sessionStorage
  const saveFormToSession = useCallback(() => {
    try {
      const currentValues = methods.getValues();
      sessionStorage.setItem(formStorageKey, JSON.stringify(currentValues));
    } catch (e) {
      console.error("Failed to save form data to sessionStorage:", e);
    }
  }, [methods, formStorageKey]);

  // Restore form data from sessionStorage on mount when step > 0
  useEffect(() => {
    const stepParam = parseInt(searchParams.get("step") || "0", 10);
    if (stepParam > 0) {
      try {
        const stored = sessionStorage.getItem(formStorageKey);
        if (stored) {
          const data = JSON.parse(stored);
          setValues(data);
          isRestoredRef.current = true;
          isFirstRender.current = false;
        }
      } catch (e) {
        console.error("Failed to restore form data from sessionStorage:", e);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ahuType = methods.watch("Type");
  const coils = methods.watch("coils");
  const isLinePrime = baseUnitType === AhuType.LINE_PRIME;

  useEffect(() => {
    if (projectItemId && isEditing && !isRestoredRef.current) {
      getProjectItem(projectItemId)
        .then((res) => {
          const mappedData = {
            id: res.data.id,
            projId: res.data.projectId,
            projIdent: res.data.ident,
            projName: res.data.name,
            projNumber: res.data.number,
            revisionNumber: res.data.revisionNumber,
            area: res.data.area,
            height: res.data.height,
            buildingType: res.data.buildingType,
            changeRate: res.data.changeRate,
            dropSupply: res.data.dropSupply,
            flowSupply: res.data.flowSupply,
            flowReturn: res.data.flowReturn,
            dropReturn: res.data.dropReturn,
            // Requirements (moved to Type of Unit)
            ecoDesign: res.data.ecoDesign,
            casingQuality: res.data.casingQuality || "XPS",
            // Type of unit
            installation: res.data.installation,
            unitType: res.data.unitType,
            coils: res.data.coils,
            heatRecovery: res.data.heatRecovery,
            heatRecoveryPlateType:
              res.data.sizeOfUnit <= 5
                ? "Rotary"
                : res.data.sizeOfUnit > 10
                  ? "Enthalpy"
                  : "Plate",
            controls: res.data.controls,
            unitDivision: res.data.unitDivision,
            sizeOfUnit: res.data.sizeOfUnit,

            airflowDirection: res.data.airflowDirection,

            // Filtering
            doubleStageFiltering: res.data.doubleStageFiltering,
            filteringAdvancedSettings: res.data.filteringAdvancedSettings,
            fineFilterType: res.data.fineFilterType,
            fineFilterClassSupply: res.data.fineFilterClassSupply,
            fineFilterClassReturn: res.data.fineFilterClassReturn,
            // Fan
            fanAdvancedSettings: res.data.fanAdvancedSettings,
            fanType: res.data.fanType,
            //Summer conditions
            summerOutdoorTemperature: res.data.summerOutdoorTemperature,
            summerWetBulbTemperature: res.data.summerWetBulbTemperature,
            summerDewPointTemperature: res.data.summerDewPointTemperature,
            summerOutdoorHumidity: res.data.summerOutdoorHumidity,
            summerIndoorTemperature: res.data.summerIndoorTemperature,
            summerIndoorHumidity: res.data.summerIndoorHumidity,
            summerSupplyTemperature: res.data.summerSupplyTemperature,
            //Cooler
            coolerType: res.data.coolerType,
            coolerConcentration: res.data.coolerConcentration,
            coolerTemperatureIn: res.data.coolerTemperatureIn,
            coolerAirInputParam: res.data.coolerAirInputParam,
            coolerAirInputParamValue: res.data.coolerAirInputParamValue,
            coolerWaterInputParam: res.data.coolerWaterInputParam,
            coolerWaterInputParamValue: res.data.coolerWaterInputParamValue,
            //Winter conditions
            winterOutdoorTemperature: res.data.winterOutdoorTemperature,
            winterOutdoorHumidityRatio: res.data.winterOutdoorHumidityRatio,
            elevation: res.data.elevation,
            winterOutdoorHumidity: res.data.winterOutdoorHumidity,
            winterIndoorTemperature: res.data.winterIndoorTemperature,
            winterIndoorHumidity: res.data.winterIndoorHumidity,
            //Heater
            heaterType: res.data.heaterType,
            heaterConcentration: res.data.heaterConcentration,
            heaterTemperatureIn: res.data.heaterTemperatureIn,
            heaterAirInputParam: res.data.heaterAirInputParam,
            heaterAirInputParamValue: res.data.heaterAirInputParamValue,
            heaterWaterInputParam: res.data.heaterWaterInputParam,
            heaterWaterInputParamValue: res.data.heaterWaterInputParamValue,
            airFlowCalc: true,
            Type: unitTypeReverseMapping[res.data.unitType],
          };

          setValues(mappedData);

          getProject(res.data.projectId).then((res) => {
            setValues({
              winterOutdoorHumidityRatio: res.data.winterOutdoorHumidityRatio,
            });
          });
        })
        .catch((err) => {
          snackbarDispatch(
            setSnackbar(true, "error", t("snackbar.fetchingDataError")),
          );
        });
    }
  }, [projectItemId, isEditing]);

  const onNext = async (_, stepId) => {
    // Trigger validation for all fields first
    const isValid = await methods.trigger();

    if (!isValid) {
      return Promise.reject(methods.formState.errors);
    }

    // If valid, proceed with submit
    const data = methods.getValues();
    await handleSubmit(data, stepId);

    // Save form data (including API response updates) to sessionStorage
    saveFormToSession();
  };

  const handleUnitInfoSubmit = async (data) => {
    try {
      // For Compact units the user can select Horizontal (1) or Vertical (7),
      // so use data.Type when it is a valid Compact sub-type, otherwise fall back
      // to baseUnitType.
      const compactSubTypes = [AhuType.COMPACT, AhuType.COMPACT_VERTICAL];
      const effectiveType =
        baseUnitType === AhuType.COMPACT && compactSubTypes.includes(data.Type)
          ? data.Type
          : baseUnitType;
      const res = await getAirFlowCalculation(
        data.flowSupply,
        isLinePrime ? data.flowSupply : data.flowReturn,
        effectiveType,
      );
      if (!res.data.data.length) {
        dispatchError(t("projectinformation.flowValError"));
        return;
      }
      const unitTypes = res.data.data;
      // Store unitTypes in form state for TypeOfUnit to access
      setValues({ sizeOfUnitTypes: unitTypes });
      return unitTypes;
    } catch (error) {
      dispatchError(error.message || t("projectinformation.flowValError"));
      throw error;
    }
  };

  const handleFilteringSubmit = async (data) => {
    try {
      const response = await modularFilterApi(data);
      if (response.statusCode >= 200 && response.statusCode < 300) {
        return response.data;
      } else {
        dispatchError(response.message || t("projectinformation.flowValError"));
        throw new Error(response.message || "modularFilter validation failed");
      }
    } catch (error) {
      dispatchError(error.message || t("projectinformation.flowValError"));
      throw error;
    }
  };

  const handleFanSubmit = async (data) => {
    try {
      const response = await fanValApi(data);
      if (response.statusCode >= 200 && response.statusCode < 300) {
        return response.data;
      } else {
        dispatchError(response.message || t("projectinformation.flowValError"));
        throw new Error(response.message || "fanVal validation failed");
      }
    } catch (error) {
      dispatchError(error.message || t("projectinformation.flowValError"));
      throw error;
    }
  };

  // LINE:PRIME Connection step handler
  const handleConnectionSubmit = async (data) => {
    setIsLoading(true);
    setLoadingMessage(t("loader.message") || "Loading...");
    try {
      await handleConnectionRecalculate(data);
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handleConnectionRecalculate = async (data) => {
    try {
      const response = await damperValApi(data);

      if (response.statusCode >= 200 && response.statusCode < 300) {
        const result = response.data || {};
        const nextValues = {};

        const freshAirDamper =
          result.freshAirDamper || result.FreshAirDamper || {};
        const supplyAirDamper =
          result.supplyAirDamper || result.SupplyAirDamper || {};
        const returnAirDamper =
          result.returnAirDamper || result.ReturnAirDamper || {};
        const freshRegulationDamper =
          result.freshRegulationDamper || result.FreshRegulationDamper || {};
        const supplyRegulationDamper =
          result.supplyRegulationDamper || result.SupplyRegulationDamper || {};
        const returnRegulationDamper =
          result.returnRegulationDamper || result.ReturnRegulationDamper || {};

        const applyAccessoryValues = (prefix, accessory) => {
          const width = getAccessoryField(result, [
            `${prefix}Width`,
            `${prefix.charAt(0).toUpperCase() + prefix.slice(1)}Width`,
          ]);
          const height = getAccessoryField(result, [
            `${prefix}Height`,
            `${prefix.charAt(0).toUpperCase() + prefix.slice(1)}Height`,
          ]);
          const airVelocity = getAccessoryField(result, [
            `${prefix}AirVelocity`,
            `${prefix.charAt(0).toUpperCase() + prefix.slice(1)}AirVelocity`,
          ]);

          const resolvedWidth =
            width ?? getAccessoryField(accessory, ["width", "Width"]);
          const resolvedHeight =
            height ?? getAccessoryField(accessory, ["height", "Height"]);
          const resolvedAirVelocity =
            airVelocity ??
            getAccessoryField(accessory, ["airVelocity", "AirVelocity"]);
          const resolvedEnabled = getAccessoryField(accessory, [
            "isEnabled",
            "IsEnabled",
          ]);

          if (resolvedWidth !== undefined) {
            nextValues[`${prefix}Width`] = resolvedWidth.toString();
          }

          if (resolvedHeight !== undefined) {
            nextValues[`${prefix}Height`] = resolvedHeight.toString();
          }

          if (resolvedAirVelocity !== undefined) {
            nextValues[`${prefix}AirVelocity`] =
              resolvedAirVelocity.toString();
          }

          if (resolvedEnabled !== undefined) {
            nextValues[`${prefix}Damper`] = Boolean(resolvedEnabled);
          }
        };

        applyAccessoryValues(
          "connectionFreshAir",
          Object.keys(freshAirDamper).length > 0
            ? freshAirDamper
            : freshRegulationDamper,
        );
        applyAccessoryValues(
          "connectionSupplyAir",
          Object.keys(supplyAirDamper).length > 0
            ? supplyAirDamper
            : supplyRegulationDamper,
        );

        const returnAirEnabled = getAccessoryField(result, [
          "connectionReturnAirEnabled",
          "ConnectionReturnAirEnabled",
        ]);

        if (returnAirEnabled !== undefined) {
          nextValues.connectionReturnAirEnabled = Boolean(returnAirEnabled);
        }

        if (
          Object.keys(returnAirDamper).length > 0 ||
          Object.keys(returnRegulationDamper).length > 0 ||
          nextValues.connectionReturnAirEnabled ||
          data.connectionReturnAirEnabled
        ) {
          applyAccessoryValues(
            "connectionReturnAir",
            Object.keys(returnAirDamper).length > 0
              ? returnAirDamper
              : returnRegulationDamper,
          );
        }

        if (Object.keys(nextValues).length > 0) {
          setValues(nextValues);
        }

        return response.data;
      }

      dispatchError(response.message || t("projectinformation.flowValError"));
      throw new Error(response.message || "damperVal validation failed");
    } catch (error) {
      dispatchError(error.message || t("projectinformation.flowValError"));
      throw error;
    }
  };

  // LINE:PRIME Fan step handlers
  const handleLinePrimeFanLoad = async (data) => {
    try {
      setValues({ linePrimeFanCalculating: true });
      const response = await ziehlAbeggFanApi(data);
      if (response.statusCode >= 200 && response.statusCode < 300) {
        const fanTableData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.fans)
            ? response.data.fans
            : [];

        setValues({
          linePrimeFanTableData: fanTableData,
          linePrimeFanCalculating: false,
        });
        return fanTableData;
      } else {
        dispatchError(response.message || t("projectinformation.flowValError"));
        setValues({ linePrimeFanCalculating: false });
        throw new Error(response.message || "ziehlAbeggFan load failed");
      }
    } catch (error) {
      setValues({ linePrimeFanCalculating: false });
      dispatchError(error.message || t("projectinformation.flowValError"));
      throw error;
    }
  };

  const handleLinePrimeFanCalculate = (data) => {
    const selectedFan = data.linePrimeSelectedFan;

    setValues({
      linePrimeFanCalculated: true,
      fanChartImage: selectedFan?.fanChartImage || null,
      linePrimeFanCalculateRequested: false,
    });
  };

  const handleOperatingPointSubmit = async (data) => {
    try {
      const response = await recValApi(
        data,
        () => getRecVal(data.heatRecovery, baseUnitType),
        () => getCoilVal(data.coils, baseUnitType, data.Type),
      );

      if (response.statusCode >= 200 && response.statusCode < 300) {
        // Store recVal response in form state
        setValues({
          recCalculationResponse: response.data,
          summerRecCalculation: response.data.summerRecCalculation,
          winterRecCalculation: response.data.winterRecCalculation,
        });
        return response.data;
      } else {
        dispatchError(response.message || t("projectinformation.flowValError"));
        throw new Error(response.message || "recVal validation failed");
      }
    } catch (error) {
      dispatchError(error.message || t("projectinformation.flowValError"));
      throw error;
    }
  };

  const handleHeatPumpSubmit = async (data) => {
    try {
      const response = await heatValApi(data);

      if (response.statusCode >= 200 && response.statusCode < 300) {
        // Store response data if needed
        if (response.data) {
          const {
            dxSupply: {
              winter: { airHumidityOut, airTempOut },
            },
          } = response.data;
          // Store in form if needed for later use
          setValues({
            winterRecCalculation: {
              sup_AirTempOut: airTempOut,
              sup_HumidityOut: airHumidityOut,
            },
          });
        }

        // Set coils to "DX" as required
        setValues({ coils: "DX", heatPumpLoading: false, heatPumpError: null });
        return response.data;
      } else {
        const errorMsg =
          response.message || t("projectinformation.flowValError");
        dispatchError(errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      const errorMsg = error.message || t("projectinformation.flowValError");
      dispatchError(errorMsg);
      throw error;
    }
  };

  // Simplified validation function
  const areMandatoryFieldsFilled = useCallback(
    (proj) => {
      const commonFields = [
        "projName",
        "dropSupply",
        "flowSupply",
        "ecoDesign",
        "casingQuality",
        "installation",
        "Type",
        "summerOutdoorTemperature",
        "summerOutdoorHumidity",
        "summerIndoorTemperature",
        "summerIndoorHumidity",
        "winterIndoorTemperature",
        "winterIndoorHumidity",
        "winterOutdoorTemperature",
        "winterOutdoorHumidity",
      ];

      let requiredFields;

      if (isLinePrime) {
        requiredFields = [...commonFields, "servicingSide", "sizeOfUnit"];
      } else {
        requiredFields = [
          ...commonFields,
          "area",
          "airflowDirection",
          "height",
          "buildingType",
          "changeRate",
          "flowReturn",
          "dropReturn",
          "heatRecovery",
          "unitDivision",
          "summerWetBulbTemperature",
          "summerDewPointTemperature",
          "doubleStageFiltering",
          "fineFilterType",
          "supplyAirFilterClass",
          "fanAdvancedSettings",
          "fanMode",
        ];
      }

      const missingFields = requiredFields.filter(
        (field) => !proj[field] && proj[field] !== 0,
      );

      if (missingFields.length > 0) {
        dispatchError(t("snackbar.mandatoryFields"));
        return false;
      }

      // Check airflowDirection if heatRecovery is set
      if (
        proj.heatRecovery &&
        proj.heatRecovery !== "None" &&
        !proj.airflowDirection
      ) {
        dispatchError(t("snackbar.mandatoryFields"));
        return false;
      }

      return true;
    },
    [dispatchError, t, isLinePrime],
  );

  const handleCalculationSubmit = async (data) => {
    try {
      // Validate mandatory fields
      if (!areMandatoryFieldsFilled(data)) {
        return;
      }

      const unitTypeDisplayMap = {
        [AhuType.COMPACT]: "Compact:Prime",
        [AhuType.COMPACT_VERTICAL]: "Compact:Prime Vertical",
        [AhuType.ENERGY_PRIME_STANDARD]: "Energy:Prime Standard",
        [AhuType.ENERGY_PRIME_GENIUS]: "Energy:Prime Genius",
        [AhuType.AQUA_PRIME_STANDARD]: "Aqua:Prime Standard",
        [AhuType.AQUA_PRIME_GENIUS]: "Aqua:Prime Genius",
        [AhuType.LINE_PRIME]: "Line:Prime",
      };

      // Prepare request data - only include fields from the example payload
      const requestData = {
        projId: data.projId,
        projIdent: data.projIdent,
        projName: data.projName,
        projNumber: data.projNumber,
        revisionNumber: data.revisionNumber,
        area: data.area,
        height: data.height,
        buildingType: data.buildingType,
        changeRate: data.changeRate,
        dropSupply: data.dropSupply,
        flowSupply: data.flowSupply,
        flowReturn: data.flowReturn,
        dropReturn: data.dropReturn,
        ecoDesign: data.ecoDesign,
        maintenance: data.maintenance || "none",
        casingQuality: data.casingQuality,
        installation: data.installation,
        unitType: unitTypeDisplayMap[data.Type] || data.Type,
        coils: data.coils,
        heatRecovery: data.heatRecovery,
        sizeOfUnit: data.sizeOfUnit,
        controls: data.controls,
        unitDivision: data.unitDivision,
        heatRecoveryPlateType: data.heatRecoveryPlateType,
        airflowDirection: data.airflowDirection,
        summerOutdoorTemperature: data.summerOutdoorTemperature,
        summerWetBulbTemperature: data.summerWetBulbTemperature,
        summerDewPointTemperature: data.summerDewPointTemperature,
        summerOutdoorHumidity: data.summerOutdoorHumidity,
        summerIndoorTemperature: data.summerIndoorTemperature,
        summerIndoorHumidity: data.summerIndoorHumidity,
        summerSupplyTemperature: data.summerSupplyTemperature,
        summerRecTemperatureOut: data.summerRecTemperatureOut,
        summerRecHumidityOut: data.summerRecHumidityOut,
        coolerType: data.coolerType,
        coolerConcentration: data.coolerConcentration,
        coolerTemperatureIn: data.coolerTemperatureIn,
        designDryBulbTemperature: data.designDryBulbTemperature,
        designDewPointTemperature: data.designDewPointTemperature,
        designWetBulbTemperature: data.designWetBulbTemperature,
        winterOutdoorTemperature: data.winterOutdoorTemperature,
        winterOutdoorHumidityRatio: data.winterOutdoorHumidityRatio,
        elevation: data.elevation,
        winterOutdoorHumidity: data.winterOutdoorHumidity,
        winterIndoorTemperature: data.winterIndoorTemperature,
        winterIndoorHumidity: data.winterIndoorHumidity,
        winterSupplyTemperature: data.winterSupplyTemperature,
        winterRecTemperatureOut: data.winterRecTemperatureOut,
        heaterType: data.heaterType,
        heaterConcentration: data.heaterConcentration || 0,
        heaterTemperatureIn: data.heaterTemperatureIn,
        doubleStageFiltering: data.doubleStageFiltering,
        filteringAdvancedSettings: data.filteringAdvancedSettings,
        fineFilterType: data.fineFilterType,
        fineFilterClassSupply: data.supplyAirFilterClass,
        fineFilterClassReturn: data.returnAirFilterClass,
        fineFilterClassFresh: data.freshAirFilterClass,
        fanAdvancedSettings: data.fanAdvancedSettings,
        fanType: data.fanType,
        damperHeight: data.damperHeight,
        damperWidth: data.damperWidth,
        fanInstallationHeight: data.fanInstallationHeight,
        fanInstallationWidth: data.fanInstallationWidth,
        internalHeight: data.internalHeight,
        internalWidth: data.internalWidth,
        internalLength: data.internalLength,
        externalWidth: data.externalWidth,
        externalHeight: data.externalHeight,
        fan: "K3G280PS10J5;ECF(K)6D280-PLHDAL1Y-RF" || data.fan,
        heatRecoverySize: data.heatRecoverySize,
        guid: data.guid,
        unitTypeId: toNumber(data.sizeOfUnit),
        airFlowSupply: toNumber(data.flowSupply),
        airInputParamsHeater: data.airInputParamsHeater || "Supply Temperature",
        winterSupplyTemperatureState:
          data.winterSupplyTemperatureState || "Supply Temperature",
        airInputParamsCooler: data.airInputParamsCooler || "Supply Temperature",
        summerSupplyTemperatureState:
          data.summerSupplyTemperatureState || "Supply Temperature",
        SummerAirTemperatureIn: data.SummerAirTemperatureIn || 0,
        WinterAirTemperatureIn: data.WinterAirTemperatureIn || 0,
        coilInputTemperatureCooler:
          data.coilInputTemperatureCooler ||
          data.summerRecTemperatureOut ||
          "0",
        coilInputTemperatureHeater:
          data.coilInputTemperatureHeater ||
          data.winterRecTemperatureOut ||
          "0",
        coilInputHumidityCooler:
          data.coilInputHumidityCooler || data.summerRecHumidityOut || "0",
        coilInputHumidityHeater:
          data.coilInputHumidityHeater || data.winterRecHumidityOut || "0",
        heaterAirInputParam: data.heaterAirInputParam || "Supply Temperature",
        heaterAirInputParamValue: data.heaterAirInputParamValue || 0,
        heaterWaterInputParam: data.heaterWaterInputParam || "Temperature out",
        heaterWaterInputParamValue: data.heaterWaterInputParamValue || 0,
        coolerAirInputParam: data.coolerAirInputParam || "Supply Temperature",
        coolerAirInputParamValue: data.coolerAirInputParamValue || 0,
        coolerWaterInputParam: data.coolerWaterInputParam || "Temperature out",
        coolerWaterInputParamValue: data.coolerWaterInputParamValue || 0,
        humidityRatio:
          data.humidityRatio || data.winterOutdoorHumidityRatio || 0,
        designWinterOutdoorTemperature:
          data.designWinterOutdoorTemperature || 0,
        winterRecHumidityOut: data.winterRecHumidityOut || "0",
        localDateTime: new Date(),
        timeZone: new Date().getTimezoneOffset(),
        coolerCapacity:
          data.coolerAirInputParam === "Supply Temperature"
            ? 0
            : data.coolerAirInputParamValue || 0,
        coolerFlow:
          data.coolerWaterInputParam === "Temperature out"
            ? 0
            : data.coolerWaterInputParamValue || 0,
        heaterCapacity:
          data.heaterAirInputParam === "Supply Temperature"
            ? 0
            : data.heaterAirInputParamValue || 0,
        heaterFlow:
          data.heaterWaterInputParam === "Temperature out"
            ? 0
            : data.heaterWaterInputParamValue || 0,
        CoolerTemperatureOut:
          data.coolerWaterInputParam !== "Temperature out"
            ? "-99"
            : (data.coolerWaterInputParamValue || 0).toString(),
        HeaterTemperatureOut:
          data.heaterWaterInputParam !== "Temperature out"
            ? "-99"
            : (data.heaterWaterInputParamValue || 0).toString(),
        FanMode: data.fanMode || "Premium",
        Automatics: data.Automatics ?? false,
        Energetics: data.Energetics ?? false,
        AirflowLeading: data.AirflowLeading ?? false,
        ServiceOpening: data.ServiceOpening ?? false,
        EnergyVAV: data.EnergyVAV ?? false,
        EnergyVAVCount: data.EnergyVAV ? data.EnergyVAVCount || 0 : 0,
        Lights: data.Lights ?? false,
        Roof: data.Roof ?? false,
        SpareFilters: data.SpareFilters ?? false,
        PressureLeading: data.PressureLeading ?? false,
        Periphery: data.Periphery ?? 0,
        HeaterMixingValve: data.HeaterMixingValve ?? false,
        CoolerMixingValve: data.CoolerMixingValve ?? false,
      };

      // Add LINE:PRIME-specific fields
      if (isLinePrime) {
        requestData.servicingSide = data.servicingSide;
        requestData.linePrimeMotorType = data.linePrimeMotorType;
        requestData.linePrimeSelectedFan = data.linePrimeSelectedFan;
        requestData.linePrimeFilterFirstStageEnabled =
          data.linePrimeFilterFirstStageEnabled;
        requestData.linePrimeFilterFirstStageClass =
          data.linePrimeFilterFirstStageEnabled
            ? data.linePrimeFilterFirstStageClass
            : null;
        requestData.linePrimeFilterSecondStageEnabled =
          data.linePrimeFilterSecondStageEnabled;
        requestData.linePrimeFilterSecondStageClass =
          data.linePrimeFilterSecondStageEnabled
            ? data.linePrimeFilterSecondStageClass
            : null;
        // Connection data
        requestData.connectionFreshAirWidth = data.connectionFreshAirWidth;
        requestData.connectionFreshAirHeight = data.connectionFreshAirHeight;
        requestData.connectionFreshAirAirVelocity =
          data.connectionFreshAirAirVelocity;
        requestData.connectionFreshAirDamper = data.connectionFreshAirDamper;
        requestData.connectionFreshAirFlexible =
          data.connectionFreshAirFlexible;
        requestData.connectionSupplyAirWidth = data.connectionSupplyAirWidth;
        requestData.connectionSupplyAirHeight = data.connectionSupplyAirHeight;
        requestData.connectionSupplyAirAirVelocity =
          data.connectionSupplyAirAirVelocity;
        requestData.connectionSupplyAirDamper = data.connectionSupplyAirDamper;
        requestData.connectionSupplyAirFlexible =
          data.connectionSupplyAirFlexible;
        requestData.freshAirDamper = buildConnectionAccessory(
          data.connectionFreshAirWidth,
          data.connectionFreshAirHeight,
          data.connectionFreshAirDamper,
          data.connectionFreshAirAirVelocity,
        );
        requestData.freshAirFlexi = {
          width: toNumber(data.connectionFreshAirWidth),
          height: toNumber(data.connectionFreshAirHeight),
          isEnabled: Boolean(data.connectionFreshAirFlexible),
        };
        requestData.supplyAirDamper = buildConnectionAccessory(
          data.connectionSupplyAirWidth,
          data.connectionSupplyAirHeight,
          data.connectionSupplyAirDamper,
          data.connectionSupplyAirAirVelocity,
        );
        requestData.supplyAirFlexi = {
          width: toNumber(data.connectionSupplyAirWidth),
          height: toNumber(data.connectionSupplyAirHeight),
          isEnabled: Boolean(data.connectionSupplyAirFlexible),
        };
        if (data.connectionReturnAirEnabled) {
          requestData.connectionReturnAirEnabled = true;
          requestData.connectionReturnAirWidth = data.connectionReturnAirWidth;
          requestData.connectionReturnAirHeight =
            data.connectionReturnAirHeight;
          requestData.connectionReturnAirAirVelocity =
            data.connectionReturnAirAirVelocity;
          requestData.connectionReturnAirDamper =
            data.connectionReturnAirDamper;
          requestData.connectionReturnAirFlexible =
            data.connectionReturnAirFlexible;
          requestData.returnAirDamper = buildConnectionAccessory(
            data.connectionReturnAirWidth,
            data.connectionReturnAirHeight,
            data.connectionReturnAirDamper,
            data.connectionReturnAirAirVelocity,
          );
          requestData.returnAirFlexi = {
            width: toNumber(data.connectionReturnAirWidth),
            height: toNumber(data.connectionReturnAirHeight),
            isEnabled: Boolean(data.connectionReturnAirFlexible),
          };
        } else {
          requestData.connectionReturnAirEnabled = false;
          requestData.returnAirDamper = buildConnectionAccessory(
            0,
            0,
            false,
            0,
          );
          requestData.returnAirFlexi = {
            width: 0,
            height: 0,
            isEnabled: false,
          };
        }
      }

      // Determine if editing or creating
      if (isEditing) {
        // Update existing project item
        const response = await editProjectItem({
          ...requestData,
          id: projectItemId,
        });
        if (response.statusCode >= 200 && response.statusCode < 300) {
          sessionStorage.removeItem(formStorageKey);
          snackbarDispatch(
            setSnackbar(
              true,
              "success",
              t("snackbar.editProjectItemSuccess"),
              "/project-item-revision",
              { replace: true, state: { id: data.id } },
            ),
          );
        } else {
          dispatchError(
            response.message || t("snackbar.createProjectItemError"),
          );
        }
      } else {
        // Create new project item
        const response = await createProjectItemBlob(requestData);
        if (response.statusCode >= 200 && response.statusCode < 300) {
          sessionStorage.removeItem(formStorageKey);
          snackbarDispatch(
            setSnackbar(
              true,
              "success",
              t("snackbar.createProjectItemSuccess"),
              "/project-item-revision",
              {
                replace: true,
                state: {
                  projectItem: data,
                  id: response.data,
                },
              },
            ),
          );
        } else {
          dispatchError(
            response.message || t("snackbar.createProjectItemError"),
          );
        }
      }
    } catch (error) {
      dispatchError(error.message || t("snackbar.createProjectItemError"));
    }
  };

  const handleCoilsSubmit = async (data) => {
    try {
      const response = await coilsValApi({
        ...data,
        coils:
          data.Type === AhuType.ENERGY_PRIME_GENIUS
            ? "heaterDefrost"
            : data.coils,
      });
      if (response.statusCode >= 200 && response.statusCode < 300) {
        return response.data;
      } else {
        dispatchError(response.message || t("projectinformation.flowValError"));
        throw new Error(response.message || "coilsVal validation failed");
      }
    } catch (error) {
      dispatchError(error.message || t("snackbar.createProjectItemError"));
      throw error;
    }
  };

  const handleSubmit = async (data, stepId) => {
    try {
      switch (stepId) {
        case Steps.UNIT_INFO:
          setIsLoading(true);
          setLoadingMessage(t("loader.message") || "Loading...");
          try {
            await handleUnitInfoSubmit(data);
          } finally {
            setIsLoading(false);
            setLoadingMessage("");
          }
          break;
        case Steps.TYPE_OF_UNIT:
          console.log(
            "Finished Type of Unit step, data after finished step=",
            data,
          );
          break;
        case Steps.OPERATING_POINT:
          setIsLoading(true);
          setLoadingMessage(t("loader.message") || "Loading...");
          try {
            await handleOperatingPointSubmit(data);
          } finally {
            setIsLoading(false);
            setLoadingMessage("");
          }
          break;
        case Steps.CONNECTION:
          await handleConnectionSubmit(data);
          break;
        case Steps.HEAT_RECOVERY:
          break;
        case Steps.HEAT_PUMP:
          setIsLoading(true);
          setLoadingMessage(t("loader.message") || "Loading...");
          try {
            await handleHeatPumpSubmit(data);
          } finally {
            setIsLoading(false);
            setLoadingMessage("");
          }
          break;
        case Steps.COILS:
          setIsLoading(true);
          setLoadingMessage(t("loader.message") || "Loading...");
          try {
            if (data.coils !== "without") {
              await handleCoilsSubmit(data);
            }
          } finally {
            setIsLoading(false);
            setLoadingMessage("");
          }
          break;
        case Steps.FILTERING:
          if (isLinePrime) {
            setIsLoading(true);
            setLoadingMessage(t("loader.message") || "Loading...");
            try {
              await handleFilteringSubmit(data);
            } finally {
              setIsLoading(false);
              setLoadingMessage("");
            }
          }
          break;
        case Steps.FAN:
          if (isLinePrime) {
            // LINE:PRIME fan validation: Ziehl-Abegg requires motor type; EBM does not
            if (!data.linePrimeMotorType && data.linePrimeFanType !== "EBM Papst") {
              dispatchError(t("linePrimeFan.motorRequired"));
              throw new Error("Motor type required");
            }
            if (!data.linePrimeSelectedFan) {
              dispatchError(t("linePrimeFan.fanRequired"));
              throw new Error("Fan selection required");
            }
            if (!data.linePrimeFanCalculated) {
              dispatchError(t("linePrimeFan.calculateRequiredForNext"));
              throw new Error("Calculate required");
            }
            setIsLoading(true);
            setLoadingMessage(t("loader.message") || "Loading...");
            try {
              await ziehlAbeggFanSelectApi({
                Guid: data.guid,
                FanId: data.linePrimeSelectedFan?.ident ?? data.linePrimeSelectedFan?.id,
              });
            } finally {
              setIsLoading(false);
              setLoadingMessage("");
            }
          } else {
            setIsLoading(true);
            setLoadingMessage(t("loader.message") || "Loading...");
            try {
              await handleFanSubmit(data);
            } finally {
              setIsLoading(false);
              setLoadingMessage("");
            }
          }
          break;
        case Steps.CALCULATION:
          setIsLoading(true);
          setLoadingMessage(t("loader.message") || "Loading...");
          try {
            await handleCalculationSubmit(data);
          } finally {
            setIsLoading(false);
            setLoadingMessage("");
          }
          break;
        default:
          console.log({ data });
          break;
      }
    } catch (error) {
      setIsLoading(false);
      setLoadingMessage("");
      // Error handling is done in individual submit handlers
      return Promise.reject(error);
    }
  };

  const onPrevious = (stepId) => {
    console.log(stepId);
  };

  // Use form field calculations hook
  useFormFieldCalculations(methods.watch, methods.setValue);

  // LINE:PRIME: Watch for fan calculate requests
  const linePrimeFanCalculateRequested = methods.watch(
    "linePrimeFanCalculateRequested",
  );
  const linePrimeMotorType = methods.watch("linePrimeMotorType");
  const linePrimeFanType = methods.watch("linePrimeFanType");

  useEffect(() => {
    if (!isLinePrime || !linePrimeFanCalculateRequested) return;
    const data = methods.getValues();
    handleLinePrimeFanCalculate(data);
  }, [linePrimeFanCalculateRequested]); // eslint-disable-line react-hooks/exhaustive-deps

  // LINE:PRIME: Load fan table when motor type changes
  useEffect(() => {
    if (!isLinePrime || !linePrimeMotorType) return;
    const data = methods.getValues();
    handleLinePrimeFanLoad(data);
  }, [linePrimeMotorType]); // eslint-disable-line react-hooks/exhaustive-deps

  // LINE:PRIME: Load fan table when EBM Papst is selected (no motor type required)
  useEffect(() => {
    if (!isLinePrime || linePrimeFanType !== "EBM Papst") return;
    const data = methods.getValues();
    handleLinePrimeFanLoad(data);
  }, [linePrimeFanType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch project data on mount to populate operating point fields
  useEffect(() => {
    if (!projectId || isFirstRender.current === false) {
      return;
    }

    isFirstRender.current = false;

    getProject(projectId)
      .then((response) => {
        if (response.statusCode >= 200 && response.statusCode < 300) {
          const projectData = response.data;

          const operatingPointData = {
            summerOutdoorTemperature: projectData.summerOutdoorTemperature || 0,
            summerWetBulbTemperature: projectData.summerWetBulbTemperature || 0,
            summerDewPointTemperature:
              projectData.summerDewPointTemperature || 0,
            summerOutdoorHumidity: projectData.summerOutdoorHumidity || 0,
            winterOutdoorTemperature: projectData.winterOutdoorTemperature || 0,
            winterOutdoorHumidity: projectData.winterOutdoorHumidity || 0,
            winterOutdoorHumidityRatio:
              projectData.winterOutdoorHumidityRatio || 1,
            elevation: projectData.elevation || 1,
          };

          setValues(operatingPointData);

          // Fetch design conditions if latitude/longitude exist
          if (projectData.latitude && projectData.longitude) {
            getProjectLocationConditions(
              projectData.latitude,
              projectData.longitude,
            )
              .then((res) => {
                if (res.statusCode >= 200 && res.statusCode < 300) {
                  const designData = res.data.data.basicDesignConditions;
                  const designDataFormValues = {
                    designDryBulbTemperature:
                      designData.summerDesignConditions
                        ?.summerOutdoorTemperature || 34.6,
                    designWetBulbTemperature:
                      designData.summerDesignConditions
                        ?.summerWetBulbTemperature || 21.1,
                    designDewPointTemperature:
                      designData.summerDesignConditions
                        ?.summerDewPointTemperature || 14.3,
                  };
                  setValues(designDataFormValues);
                }
              })
              .catch((err) => {
                console.error("Error fetching design conditions:", err);
              });
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching project data:", err);
        dispatchError(
          t("snackbar.fetchingDataError") || "Error fetching project data",
        );
      });
  }, [projectId, setValues, dispatchError, t]);

  // Update coil input temperatures when recVal response is received
  const recCalculationResponse = methods.watch("recCalculationResponse");
  const summerRecCalculation = methods.watch("summerRecCalculation");
  const winterRecCalculation = methods.watch("winterRecCalculation");
  const connectionReturnAirEnabled = methods.watch("connectionReturnAirEnabled");

  useEffect(() => {
    if (!recCalculationResponse) {
      return;
    }

    const connectionValues = extractConnectionValuesFromRecCalculation(
      recCalculationResponse,
    );

    if (Object.keys(connectionValues).length > 0) {
      setValues(connectionValues);
    }
  }, [recCalculationResponse, extractConnectionValuesFromRecCalculation, setValues]);

  useEffect(() => {
    if (!connectionReturnAirEnabled || !recCalculationResponse) {
      return;
    }

    const connectionValues = extractConnectionValuesFromRecCalculation(
      recCalculationResponse,
    );

    if (
      connectionValues.connectionReturnAirWidth !== undefined ||
      connectionValues.connectionReturnAirHeight !== undefined ||
      connectionValues.connectionReturnAirAirVelocity !== undefined ||
      connectionValues.connectionReturnAirDamper !== undefined
    ) {
      setValues({
        connectionReturnAirWidth:
          connectionValues.connectionReturnAirWidth ??
          methods.getValues("connectionReturnAirWidth"),
        connectionReturnAirHeight:
          connectionValues.connectionReturnAirHeight ??
          methods.getValues("connectionReturnAirHeight"),
        connectionReturnAirAirVelocity:
          connectionValues.connectionReturnAirAirVelocity ??
          methods.getValues("connectionReturnAirAirVelocity"),
        connectionReturnAirDamper:
          connectionValues.connectionReturnAirDamper ??
          methods.getValues("connectionReturnAirDamper"),
      });
    }
  }, [
    connectionReturnAirEnabled,
    recCalculationResponse,
    extractConnectionValuesFromRecCalculation,
    setValues,
    methods,
  ]);

  useEffect(() => {
    if (
      summerRecCalculation &&
      summerRecCalculation.sup_AirTempOut !== undefined
    ) {
      setValues({
        coilInputTemperatureCooler: summerRecCalculation.sup_AirTempOut,
        coilInputHumidityCooler: summerRecCalculation.sup_HumidityOut,
        summerRecTemperatureOut: summerRecCalculation.sup_AirTempOut,
        summerRecHumidityOut: summerRecCalculation.sup_HumidityOut,
        heatPumpTemperatureInSummer: summerRecCalculation.sup_AirTempOut,
        heatPumpHumidityInSummer: summerRecCalculation.sup_HumidityOut,
      });
    }

    if (
      winterRecCalculation &&
      winterRecCalculation.sup_AirTempOut !== undefined
    ) {
      setValues({
        coilInputTemperatureHeater: winterRecCalculation.sup_AirTempOut,
        coilInputHumidityHeater: winterRecCalculation.sup_HumidityOut,
        winterRecTemperatureOut: winterRecCalculation.sup_AirTempOut,
        winterRecHumidityOut: winterRecCalculation.sup_HumidityOut,
        heatPumpTemperatureInWinter: winterRecCalculation.sup_AirTempOut,
        heatPumpHumidityInWinter: winterRecCalculation.sup_HumidityOut,
      });
    }
  }, [
    summerRecCalculation,
    winterRecCalculation,
    setValues,
      ]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Loader open={isLoading} message={loadingMessage} />
        <Stepper
          ahuType={ahuType}
          coils={coils}
          stepContent={[
            <UnitInformation key={Steps.UNIT_INFO} />,
            <TypeOfUnit key={Steps.TYPE_OF_UNIT} />,
            <OperatingPoint key={Steps.OPERATING_POINT} />,
            <Connection
              key={Steps.CONNECTION}
              onRecalculate={handleConnectionRecalculate}
            />,
            <HeatRecovery key={Steps.HEAT_RECOVERY} />,
            <HeatPump key={Steps.HEAT_PUMP} />,
            <Coils key={Steps.COILS} />,
            isLinePrime ? (
              <LinePrimeFiltering key={Steps.FILTERING} />
            ) : (
              <Filtering key={Steps.FILTERING} />
            ),
            isLinePrime ? (
              <LinePrimeFan key={Steps.FAN} />
            ) : (
              <Fan key={Steps.FAN} />
            ),
            <Calculation key={Steps.CALCULATION} />,
          ]}
          onNext={onNext}
          onPrevious={onPrevious}
        />
      </form>
    </FormProvider>
  );
};
